import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Wrench, Database } from 'lucide-react';
import { getMaintenanceStatus, toggleMaintenance, getBackupsList, triggerBackup } from './maintenance.api';
import type { MaintenanceState, BackupFile } from './maintenance.api';
import { PageContainer } from '../../../components/layout/PageContainer';
import { Stack } from '../../../components/layout/Stack';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { DataView } from '../../../components/ui/DataView';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/FormField';
import { RetentionManagerCard } from './RetentionManagerCard';

export function MaintenanceManager() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['maintenance'],
    queryFn: getMaintenanceStatus,
  });

  const [enabled, setEnabled] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState<string[]>(['group_admin', 'company_admin']);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<boolean | null>(null);

  useEffect(() => {
    if (query.data) {
      setEnabled(query.data.enabled);
      setAllowedRoles(query.data.allowedRoles ?? ['group_admin', 'company_admin']);
    }
  }, [query.data]);

  const backupsQuery = useQuery({
    queryKey: ['maintenanceBackups'],
    queryFn: getBackupsList,
  });

  const triggerMutation = useMutation({
    mutationFn: triggerBackup,
    onSuccess: () => {
      backupsQuery.refetch();
    },
  });

  const mutation = useMutation({
    mutationFn: (body: { enabled: boolean; allowedRoles?: string[] }) => toggleMaintenance(body),
    onSuccess: (result: MaintenanceState) => {
      queryClient.setQueryData(['maintenance'], result);
      if (result.enabled) localStorage.setItem('kraphene_mnt', 'true');
      else localStorage.removeItem('kraphene_mnt');
    },
  });

  const handleToggleClick = () => {
    const nextState = !enabled;
    setPendingToggle(nextState);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (pendingToggle !== null) {
      setEnabled(pendingToggle);
    }
    setConfirmOpen(false);
    setPendingToggle(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingToggle(null);
  };

  const handleApply = () => {
    mutation.mutate({ enabled, allowedRoles });
  };

  const hasChanges =
    query.data && (
      enabled !== query.data.enabled ||
      JSON.stringify([...allowedRoles].sort()) !== JSON.stringify([...(query.data.allowedRoles ?? ['group_admin', 'company_admin'])].sort())
    );

  return (
    <PageContainer size="detail" className="p-6">
      <DataView
        query={query}
        loadingSkeleton={
          <Stack gap="6">
            <Card><CardContent className="p-6"><Skeleton rows={2} /></CardContent></Card>
            <Card><CardContent className="p-6"><Skeleton rows={4} /></CardContent></Card>
            <Card><CardContent className="p-6"><Skeleton rows={3} /></CardContent></Card>
          </Stack>
        }
      >
        {(status) => (
          <Stack gap="6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusBadge
                  status={enabled ? 'warning' : 'success'}
                  label={enabled ? 'Maintenance Active' : 'System Online'}
                />
              </div>
              <span className="text-xs text-text-muted">
                Last updated: {status.updatedAt ? formatDistanceToNow(new Date(status.updatedAt), { addSuffix: true }) : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Maintenance Toggle</CardTitle>
          </CardHeader>
          <CardContent>
            <Stack gap="4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-primary font-medium">
                  {enabled ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
                </span>
                <Button
                  variant={enabled ? 'danger' : 'primary'}
                  size="sm"
                  onClick={handleToggleClick}
                  loading={mutation.isPending && pendingToggle === null}
                >
                  {enabled ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bypass Roles</CardTitle>
          </CardHeader>
          <CardContent>
            <Stack gap="4">
              <span className="text-xs text-text-secondary leading-relaxed">
                Select which user types should be allowed to bypass the maintenance lock and access the system normally (for auditing, EOD closures, and bookkeeping):
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'group_admin', label: 'Group Admin' },
                  { id: 'company_admin', label: 'Company Admin' },
                  { id: 'staff', label: 'Staff' },
                  { id: 'agent', label: 'Agent' },
                  { id: 'customer', label: 'Customer' },
                ].map((role) => {
                  const isChecked = allowedRoles.includes(role.id);
                  const isCoreAdmin = role.id === 'group_admin' || role.id === 'company_admin';
                  return (
                    <div key={role.id} className="flex items-center gap-2">
                      <Input
                        type="checkbox"
                        id={`role-${role.id}`}
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAllowedRoles([...allowedRoles, role.id]);
                          } else {
                            setAllowedRoles(allowedRoles.filter((r) => r !== role.id));
                          }
                        }}
                        className="w-4 h-4 accent-accent-500 cursor-pointer rounded"
                      />
                      <label
                        htmlFor={`role-${role.id}`}
                        className="text-sm font-medium text-text-primary cursor-pointer select-none"
                      >
                        {role.label} {isCoreAdmin && <span className="text-xs text-text-muted">(Default)</span>}
                      </label>
                    </div>
                  );
                })}
              </div>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Database Backups (Google Drive)</CardTitle>
              <Button
                variant="primary"
                size="sm"
                onClick={() => triggerMutation.mutate()}
                loading={triggerMutation.isPending}
              >
                Backup Now
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Stack gap="4">
              <span className="text-xs text-text-secondary leading-relaxed">
                Nightly backups are scheduled automatically using the environment variable configuration (default: daily at 3 AM). On-demand backups are processed in the background via BullMQ and uploaded to Google Drive.
              </span>
              
              {backupsQuery.isLoading ? (
                <Skeleton rows={3} />
              ) : !backupsQuery.data || backupsQuery.data.length === 0 ? (
                <div className="rounded-[6px] border border-border bg-surface-2 p-5 text-center text-xs text-text-secondary">
                  No remote backups found.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-[6px] border border-border">
                  <table className="min-w-full divide-y divide-border text-left text-xs">
                    <thead className="bg-surface-2 text-text-secondary font-semibold">
                      <tr>
                        <th className="p-3">Filename</th>
                        <th className="p-3">Size</th>
                        <th className="p-3">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-surface-1 text-text-primary">
                      {backupsQuery.data.map((file) => (
                        <tr key={file.filename}>
                          <td className="p-3 font-mono break-all">{file.filename}</td>
                          <td className="p-3">{(file.sizeBytes / (1024 * 1024)).toFixed(2)} MB</td>
                          <td className="p-3">{new Date(file.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Stack>
          </CardContent>
        </Card>

        <RetentionManagerCard />

        <Card>
          <CardHeader>
            <CardTitle>User Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-[6px] border border-border bg-surface-2 p-5 flex flex-col items-center text-center gap-3">
              <div className="text-text-muted opacity-40">
                <Wrench className="w-8 h-8 text-warning-500" strokeWidth={1.5} />
              </div>
              <h2 className="text-base font-semibold text-text-primary tracking-tight">
                System Maintenance
              </h2>
              <p className="text-xs text-text-secondary leading-relaxed max-w-sm">
                We're currently performing scheduled maintenance to improve our services. We'll be back online shortly. Thank you for your patience.
              </p>
            </div>
          </CardContent>
        </Card>

          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={handleApply}
              loading={mutation.isPending}
              disabled={!hasChanges}
            >
              Apply Changes
            </Button>
          </div>
        </Stack>
        )}
      </DataView>

      <Dialog open={confirmOpen} onOpenChange={(val) => !val && handleCancel()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {pendingToggle ? 'Enable Maintenance?' : 'Disable Maintenance?'}
            </DialogTitle>
            <DialogDescription>
              {pendingToggle
                ? 'Enable maintenance? This will restrict user traffic and only show the maintenance page to those without bypass roles.'
                : 'Disable maintenance? The system will immediately resume normal operation.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" size="md" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant={pendingToggle ? 'danger' : 'primary'}
              size="md"
              onClick={handleConfirm}
            >
              {pendingToggle ? 'Enable' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
